import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  Workflow,
  Play,
  Pause,
  Trash2,
  Plus,
  Clock,
  Zap,
  Calendar,
  Edit,
  BarChart3,
  Activity
} from 'lucide-react';

const AUTOMATION_API_URL = 'https://ncai-automation.bill-burkey.workers.dev';

interface WorkflowType {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'draft';
  trigger_type: string;
  trigger_config: string;
  total_executions: number;
  successful_executions: number;
  active_triggers: number;
  last_run_at: number | null;
  created_at: number;
}

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  triggerType: string;
  stepCount: number;
}

export default function Workflows() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState<'workflows' | 'templates'>('workflows');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const token = localStorage.getItem('auth_token') || '';

  // Fetch workflows
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useQuery({
    queryKey: ['workflows', organization?.id, selectedStatus],
    queryFn: async () => {
      const params = new URLSearchParams({
        organizationId: organization?.id || ''
      });
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      const response = await fetch(
        `${AUTOMATION_API_URL}/api/workflows?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.json();
    },
    enabled: !!organization?.id && !!token
  });

  // Fetch templates
  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['workflow-templates'],
    queryFn: async () => {
      const response = await fetch(`${AUTOMATION_API_URL}/api/templates`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.json();
    },
    enabled: !!token && selectedTab === 'templates'
  });

  // Activate workflow mutation
  const activateMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await fetch(
        `${AUTOMATION_API_URL}/api/workflows/${workflowId}/activate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    }
  });

  // Deactivate workflow mutation
  const deactivateMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await fetch(
        `${AUTOMATION_API_URL}/api/workflows/${workflowId}/deactivate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    }
  });

  // Delete workflow mutation
  const deleteMutation = useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await fetch(
        `${AUTOMATION_API_URL}/api/workflows/${workflowId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    }
  });

  // Create from template mutation
  const createFromTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const response = await fetch(
        `${AUTOMATION_API_URL}/api/templates/${templateId}/create`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            organizationId: organization?.id
          })
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setSelectedTab('workflows');
    }
  });

  const workflows: WorkflowType[] = workflowsData?.data?.workflows || [];
  const templates: Template[] = templatesData?.data?.templates || [];

  const getTriggerIcon = (triggerType: string) => {
    switch (triggerType) {
      case 'webhook':
        return <Zap className="w-4 h-4" />;
      case 'schedule':
        return <Calendar className="w-4 h-4" />;
      case 'event':
        return <Activity className="w-4 h-4" />;
      default:
        return <Workflow className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateSuccessRate = (workflow: WorkflowType) => {
    if (workflow.total_executions === 0) return 0;
    return Math.round((workflow.successful_executions / workflow.total_executions) * 100);
  };

  if (!organization) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Workflow Automation</h1>
            <p className="text-gray-600">Build and manage automated workflows for your business</p>
          </div>
          <button className="btn-primary flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Create Workflow
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mt-6 border-b border-gray-200">
          <button
            onClick={() => setSelectedTab('workflows')}
            className={`pb-3 px-4 font-medium transition-colors ${
              selectedTab === 'workflows'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            My Workflows
          </button>
          <button
            onClick={() => setSelectedTab('templates')}
            className={`pb-3 px-4 font-medium transition-colors ${
              selectedTab === 'templates'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Templates
          </button>
        </div>
      </div>

      {/* Workflows Tab */}
      {selectedTab === 'workflows' && (
        <>
          {/* Filters */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setSelectedStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStatus === 'all'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedStatus('active')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStatus === 'active'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setSelectedStatus('draft')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStatus === 'draft'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Draft
            </button>
            <button
              onClick={() => setSelectedStatus('inactive')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStatus === 'inactive'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Inactive
            </button>
          </div>

          {/* Workflows Grid */}
          {isLoadingWorkflows ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : workflows.length === 0 ? (
            <div className="text-center py-12 card">
              <Workflow className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No workflows yet</h3>
              <p className="text-gray-600 mb-6">Create your first workflow or start with a template</p>
              <button
                onClick={() => setSelectedTab('templates')}
                className="btn-primary"
              >
                Browse Templates
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workflows.map((workflow) => (
                <div key={workflow.id} className="card p-6 hover:shadow-lg transition-shadow">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Workflow className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{workflow.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{workflow.description}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(workflow.status)}`}>
                      {workflow.status}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Executions</p>
                      <p className="text-2xl font-bold text-gray-900">{workflow.total_executions}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Success Rate</p>
                      <p className="text-2xl font-bold text-green-600">{calculateSuccessRate(workflow)}%</p>
                    </div>
                  </div>

                  {/* Trigger Info */}
                  <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                    {getTriggerIcon(workflow.trigger_type)}
                    <span className="capitalize">{workflow.trigger_type}</span>
                    {workflow.active_triggers > 0 && (
                      <span className="ml-auto px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        {workflow.active_triggers} active
                      </span>
                    )}
                  </div>

                  {/* Last Run */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Clock className="w-4 h-4" />
                    <span>Last run: {formatDate(workflow.last_run_at)}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                    {workflow.status === 'active' ? (
                      <button
                        onClick={() => deactivateMutation.mutate(workflow.id)}
                        className="flex items-center gap-1 text-sm text-yellow-600 hover:text-yellow-700"
                        disabled={deactivateMutation.isPending}
                      >
                        <Pause className="w-4 h-4" />
                        Pause
                      </button>
                    ) : (
                      <button
                        onClick={() => activateMutation.mutate(workflow.id)}
                        className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                        disabled={activateMutation.isPending}
                      >
                        <Play className="w-4 h-4" />
                        Activate
                      </button>
                    )}
                    <button className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700">
                      <BarChart3 className="w-4 h-4" />
                      Stats
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this workflow?')) {
                          deleteMutation.mutate(workflow.id);
                        }
                      }}
                      className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 ml-auto"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Templates Tab */}
      {selectedTab === 'templates' && (
        <>
          <p className="text-gray-600 mb-6">Choose from pre-built workflow templates to get started quickly</p>

          {isLoadingTemplates ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div key={template.id} className="card p-6 hover:shadow-lg transition-shadow">
                  {/* Icon & Name */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className="text-4xl">{template.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </div>
                  </div>

                  {/* Category & Steps */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full capitalize">
                      {template.category}
                    </span>
                    <span>{template.stepCount} steps</span>
                  </div>

                  {/* Trigger Type */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                    {getTriggerIcon(template.triggerType)}
                    <span className="capitalize">{template.triggerType} trigger</span>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => createFromTemplateMutation.mutate(template.id)}
                    disabled={createFromTemplateMutation.isPending}
                    className="w-full btn-primary"
                  >
                    {createFromTemplateMutation.isPending ? 'Creating...' : 'Use Template'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
